import { Link } from "react-router-dom";

// Επαναπροσδιορισμός ανακοπών (Ν. 5221/2025). The backend sends the status on
// each case as `epanaprosdiorismos` (null when the case is not affected):
//   { role: "old", status: "pending" | "filed", window, opens, deadline, old_hearing,
//     filed_by_link }
//   { role: "application", katathesi, prothesmies: [{ code, onoma, prothesmia }],
//     auto_added_for }
// Dates are ISO (YYYY-MM-DD).
//
// An αίτηση and the ανακοπές it re-files point at each other through
//   linked_cases: [{ unique_case, gak, year, court, case_id, note }]
// where case_id is the user's own case, or null when it is not in their list.
// `filed_by_link` means only that link says an αίτηση was filed: Solon still shows
// the old Δικάσιμος. `auto_added_for` lists the ανακοπές (unique_case) an αίτηση was
// added to the user's list for.

const formatIso = (iso) => (iso ? iso.split("-").reverse().join(".") : "");

const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const daysBetween = (fromIso, toIso) =>
  Math.round(
    (Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) /
      86400000
  );

const daysLeftText = (deadline) => {
  const left = daysBetween(todayIso(), deadline);
  if (left < 0) return "η προθεσμία έληξε";
  if (left === 0) return "λήγει σήμερα";
  return left === 1 ? "σε 1 ημέρα" : `σε ${left} ημέρες`;
};

const gakLabel = (linked) => `ΓΑΚ ${linked.gak}/${linked.year}`;

const LinkedCases = ({ label, cases }) => (
  <span className="epan-alert__links">
    {label}
    {cases.map((linked) =>
      linked.case_id ? (
        <Link
          key={linked.unique_case}
          to={`/cases/${linked.case_id}`}
          // The list card handles clicks of its own.
          onClick={(event) => event.stopPropagation()}
        >
          {gakLabel(linked)}
        </Link>
      ) : (
        <span key={linked.unique_case} className="epan-alert__untracked">
          {gakLabel(linked)} (δεν είναι στη λίστα σας)
        </span>
      )
    )}
  </span>
);

const EpanaprosdiorismosAlert = ({ status }) => {
  if (!status) return null;
  const linkedCases = status.linked_cases ?? [];

  if (status.role === "application") {
    const today = todayIso();
    const next = status.prothesmies?.find((p) => p.prothesmia >= today);
    if (!next && linkedCases.length === 0) return null;
    const addedFor = linkedCases.filter((linked) =>
      status.auto_added_for?.includes(linked.unique_case)
    );
    return (
      <div className="epan-alert epan-alert--info" role="note">
        <span className="epan-alert__label">
          Αίτηση επαναπροσδιορισμού (Ν. 5221/2025)
        </span>
        {next && (
          <span className="epan-alert__result">
            {next.onoma}: έως {formatIso(next.prothesmia)} (
            {daysLeftText(next.prothesmia)})
          </span>
        )}
        {linkedCases.length > 0 && (
          <LinkedCases
            label={
              linkedCases.length === 1
                ? "Αφορά την ανακοπή:"
                : "Αφορά τις ανακοπές:"
            }
            cases={linkedCases}
          />
        )}
        {addedFor.length > 0 && (
          <span className="epan-alert__meta">
            Προστέθηκε αυτόματα στη λίστα σας, επειδή παρακολουθείτε{" "}
            {addedFor.length === 1 ? "την ανακοπή" : "τις ανακοπές"}{" "}
            {addedFor.map(gakLabel).join(", ")}.
          </span>
        )}
      </div>
    );
  }

  if (status.status === "filed") {
    const untracked = linkedCases.some((linked) => !linked.case_id);
    return (
      <div className="epan-alert epan-alert--info" role="note">
        <span className="epan-alert__label">
          Επαναπροσδιορισμός ανακοπής (Ν. 5221/2025)
        </span>
        <span className="epan-alert__result">
          Κατατέθηκε αίτηση επαναπροσδιορισμού
        </span>
        {linkedCases.length > 0 && (
          <LinkedCases
            label={linkedCases.length === 1 ? "Αίτηση:" : "Αιτήσεις:"}
            cases={linkedCases}
          />
        )}
        <span className="epan-alert__meta">
          {status.old_hearing &&
            (status.filed_by_link
              ? `Η δικάσιμος της ${formatIso(status.old_hearing)} εμφανίζεται ακόμη στο Solon, μέχρι να καταχωριστεί εκεί ο επαναπροσδιορισμός. `
              : `Η δικάσιμος της ${formatIso(status.old_hearing)} δεν ισχύει. `)}
          {linkedCases.length === 0
            ? "Η νέα δικάσιμος θα οριστεί στην αίτηση, που έχει δικό της ΓΑΚ: προσθέστε την για να την παρακολουθείτε."
            : untracked
              ? "Η νέα δικάσιμος θα οριστεί στην αίτηση: προσθέστε την για να την παρακολουθείτε."
              : "Η νέα δικάσιμος θα οριστεί στην αίτηση."}
        </span>
      </div>
    );
  }

  const notYetOpen = status.opens && todayIso() < status.opens;
  return (
    <div className="epan-alert" role="alert">
      <span className="epan-alert__label">
        <span aria-hidden="true">⚠</span> Επαναπροσδιορισμός ανακοπής (Ν.
        5221/2025)
      </span>
      <span className="epan-alert__result">
        {notYetOpen
          ? `Αίτηση επαναπροσδιορισμού από ${formatIso(status.opens)} έως ${formatIso(status.deadline)}`
          : `Αίτηση επαναπροσδιορισμού έως ${formatIso(status.deadline)} (${daysLeftText(status.deadline)})`}
      </span>
      <span className="epan-alert__meta">
        Αν δεν κατατεθεί, η ανακοπή λογίζεται ως μη ασκηθείσα και τυχόν
        προσωρινή διαταγή ή αναστολή εκτέλεσης ανακαλείται αυτοδικαίως. Ελέγξτε
        αν η υπόθεση υπάγεται: δεν υπάγονται οι ανακοπές των άρθ. 954 §4 και 973
        §6 ΚΠολΔ, ούτε όταν επισπεύδεται πλειστηριασμός πριν από το κλείσιμο του
        φακέλου της νέας διαδικασίας (άρθ. 130 §4 Ν. 5221/2025).
      </span>
    </div>
  );
};

export default EpanaprosdiorismosAlert;
